import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {ContextualMenuInputsOption} from '@Components/contextual-menu/components/contextual-menu-inputs-option';
import {isValidClickableLink, prependProtocol} from '@Utils/url.util';
import {Size} from '@Components/button';
import {ContextualMenu} from '@Components/contextual-menu';
import {showEditorNotificationMessageGrowl} from '@Components/poster-editor/library/poster-editor-library';
import {InputSuggestionsDropdown} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown';
import {useGetLinkInputSuggestions} from '@Hooks/poster-editor/useGetInputSuggestions';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from './poster-editor-item-hyperlink-panel.module.scss';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

interface PosterEditorItemHyperlinkPanelProps {
  panelId: string;
}

export default function PosterEditorItemHyperlinkPanel({...props}: PosterEditorItemHyperlinkPanelProps): ReactElement {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemCliclableLink = useGetKeyFromActiveItem('clickableLink');
  const [hyperlink, setHyperlink] = useState(activeItem?.clickableLink);
  const isInvalid = useRef(false);
  const [isChanging, setIsChanging] = useState(false);
  const linkSuggestions = useGetLinkInputSuggestions();

  useEffect((): void => {
    setHyperlink(activeItemCliclableLink);
  }, [activeItemCliclableLink]);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_hyperlink')} />;
  };

  const onChangeHandler = (val: string): void => {
    if (!isChanging) {
      setIsChanging(true);
    }
    setHyperlink(val);
  };

  const onChangeEnded = (val: string): void => {
    if (isChanging) {
      setIsChanging(false);
    }
    if (val === '') {
      return;
    }

    if (!isValidClickableLink(hyperlink ?? '')) {
      isInvalid.current = true;
      return;
    }

    const urlWithProtocol = prependProtocol(val);
    setHyperlink(urlWithProtocol);
    isInvalid.current = false;
  };

  const showErrorState = (): boolean => {
    if (hyperlink === '' || isChanging) {
      return false;
    }

    return isInvalid.current;
  };

  const onApply = (): void => {
    if (hyperlink !== undefined) {
      activeItem?.updateClickableLink(hyperlink);
      showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_link_updated'));
    }
    onClose();
  };

  const getInputSelector = (): ReactElement => {
    return (
      <ContextualMenuInputsOption
        id="sticker-interactivity-link-options"
        placeholder={window.i18next.t('pmwjs_link_placeholder_2')}
        value={hyperlink}
        hasIcon="icon-link"
        onInputChange={onChangeHandler}
        onInputChangeEnded={onChangeEnded}
        showErrorState={showErrorState()}
        errorText={window.i18next.t('pmwjs_invalid_format')}
        elementTitle={window.i18next.t('pmwjs_link_placeholder_2')}
      />
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu
        id="hyperlink-mobile-panel"
        includeLeftRightPadding={false}
        contentClassName={`spacing-m-0 spacing-p-b-4 spacing-p-r-4 spacing-p-l-4 spacing-p-t-1 ${styles.overflowHidden}`}
      >
        <InputSuggestionsDropdown
          dropdownId="item-hyperlink-panel-dropdown"
          inputSelector={getInputSelector()}
          onSuggestionSelect={onChangeHandler}
          primarySuggestionsList={linkSuggestions}
          popupAlignment={ALIGNMENT_TYPE.TOP}
          popupPosition={DROPDOWN_POSITION.FIXED}
          popupClassName={styles.suggestionsDropdown}
          inputValue={hyperlink}
        />
      </ContextualMenu>
    );
  };

  const getFooter = (): ReactElement => {
    return (
      <PanelFooter
        fullWidthPrimaryActions
        primaryActions={[
          <PanelFooterPrimaryDefaultAction
            size={Size.SMALL}
            onClick={onApply}
            key="hyperlink-add-button"
            text={window.i18next.t('pmwjs_apply')}
            disabled={showErrorState() || isChanging || hyperlink === activeItemCliclableLink}
          />,
        ]}
      />
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} panelFooter={getFooter()} />;
}
